/**
 * Editable assistance details card for the Visit edit page.
 * Renders the same assistance-specific fields used when the transaction was
 * first created in the visit wizard, pre-filled with its saved detail data.
 */

import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { Loader2, Save } from 'lucide-react'
import { AssistanceDetailFields } from '@/components/forms/assistance'
import { useCourts, useOfficeCodes } from '@/hooks/useCourts'
import { useUpdateVisitTransaction } from '@/hooks/useVisits'
import { getAssistanceLabelFromTypeId, getAssistanceTypeFromTransactionTypeId } from '@/constants/assistance-types'
import { toast } from '@/lib/toast'
import type { VisitTransaction } from '@/types/visit'

interface EditVisitTransactionProps {
  transaction: VisitTransaction
}

export function EditVisitTransaction({ transaction }: EditVisitTransactionProps) {
  const assistanceType = getAssistanceTypeFromTransactionTypeId(transaction.transaction_type_id)
  const label = getAssistanceLabelFromTypeId(transaction.transaction_type_id)

  const { data: courts = [], isLoading: courtsLoading } = useCourts()
  const { data: officeCodes = [], isLoading: officeCodesLoading } = useOfficeCodes()
  const updateTransaction = useUpdateVisitTransaction()

  const form = useForm({
    defaultValues: transaction.transaction_detail || {},
  })

  const onSubmit = async (data: Record<string, unknown>) => {
    try {
      await updateTransaction.mutateAsync({
        id: transaction.id,
        data: { transaction_detail: data },
      })
      toast.success(`${label} details updated successfully!`)
    } catch (error: any) {
      toast.error(
        `Failed to update ${label} details`,
        error.response?.data?.detail?.message || error.response?.data?.detail || error.message
      )
    }
  }

  if (!assistanceType) {
    return (
      <div className="border-b last:border-b-0 pb-6 last:pb-0">
        <h3 className="font-semibold text-gray-900 mb-2">
          {label}
          {transaction.control_number && (
            <span className="ml-2 text-sm font-mono text-gray-600">
              ({transaction.control_number})
            </span>
          )}
        </h3>
        <p className="text-sm text-gray-500">This assistance type cannot be edited here.</p>
      </div>
    )
  }

  if (courtsLoading || officeCodesLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    )
  }

  const courtsData = courts.map((c) => ({ court: c.court, code: c.code }))

  return (
    <div className="border-b last:border-b-0 pb-6 last:pb-0">
      <h3 className="font-semibold text-gray-900 mb-3">
        {label}
        {transaction.control_number && (
          <span className="ml-2 text-sm font-mono text-gray-600">
            ({transaction.control_number})
          </span>
        )}
      </h3>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <AssistanceDetailFields
            assistanceType={assistanceType}
            form={form}
            courts={courtsData}
            officeCodes={officeCodes}
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={updateTransaction.isPending}
              className="bg-primary hover:bg-primary/90"
            >
              {updateTransaction.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save {label}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
